import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { AdminAreaData } from "@/types/adminArea";
import { AnalysisConfigAdminAreaData } from "@/types/project";
import { AdminAreaSettings } from "@/types/spatial";

export interface GlobalFilterState {
  // main selections
  selectedAreaIds: string[];
  explicitSelectedAreaIds: string[];
  // temporary selections
  selectedAreaIdsTemp: string[];
  explicitSelectedAreaIdsTemp: string[];
  adminAreaSettings: AdminAreaSettings | null;
  adminAreaSettingsData: AdminAreaData | null;
  adminAreaSettingsDataTemp: AdminAreaData | null;
  linkedDatasetIds: string[];
}

export const globalFilterInitialState: GlobalFilterState = {
  selectedAreaIds: [],
  explicitSelectedAreaIds: [],
  selectedAreaIdsTemp: [],
  explicitSelectedAreaIdsTemp: [],
  adminAreaSettings: null,
  adminAreaSettingsData: null,
  adminAreaSettingsDataTemp: null,
  linkedDatasetIds: []
};

export const globalFilter = createSlice({
  name: "globalFilter",
  initialState: globalFilterInitialState,
  reducers: {
    resetGlobalFilter: () => globalFilterInitialState,
    setSelectedAreaIds: (state, action: PayloadAction<string[]>) => {
      state.selectedAreaIds = action.payload;
    },
    setExplicitSelectedAreaIds: (state, action: PayloadAction<string[]>) => {
      state.explicitSelectedAreaIds = action.payload;
    },
    setSelectedAreaIdsTemp: (state, action: PayloadAction<string[]>) => {
      state.selectedAreaIdsTemp = action.payload;
    },
    setExplicitSelectedAreaIdsTemp: (
      state,
      action: PayloadAction<string[]>
    ) => {
      state.explicitSelectedAreaIdsTemp = action.payload;
    },
    applyAdminTreeGlobalFilter: (state) => {
      state.selectedAreaIds = [...state.selectedAreaIdsTemp];
      state.explicitSelectedAreaIds = [...state.explicitSelectedAreaIdsTemp];
    },
    applyAdminTreeGlobalFilterTemp: (state) => {
      state.selectedAreaIdsTemp = [...state.selectedAreaIds];
      state.explicitSelectedAreaIdsTemp = [...state.explicitSelectedAreaIds];
    },
    setAdminAreaSettings: (
      state,
      action: PayloadAction<AdminAreaSettings | null>
    ) => {
      state.adminAreaSettings = {
        ...state.adminAreaSettings,
        ...action.payload
      };
    },
    setAdminAreaSettingsData: (
      state,
      action: PayloadAction<AdminAreaData | null>
    ) => {
      state.adminAreaSettingsData = action.payload;
    },
    setAdminAreaSettingsDataTemp: (
      state,
      action: PayloadAction<AdminAreaData | null>
    ) => {
      state.adminAreaSettingsDataTemp = action.payload;
    },
    addLinkedDatasetId: (
      state,
      action: PayloadAction<{
        datasetId: string;
      }>
    ) => {
      const { datasetId } = action.payload;

      state.linkedDatasetIds.push(datasetId);
    },
    removeLinkedDatasetId: (
      state,
      action: PayloadAction<{ datasetId: string }>
    ) => {
      const { datasetId } = action.payload;
      state.linkedDatasetIds = state.linkedDatasetIds.filter(
        (id) => id !== datasetId
      );
    },
    setGlobalAdminArea: (
      state,
      action: PayloadAction<AnalysisConfigAdminAreaData | null | undefined>
    ) => {
      state.adminAreaSettings = action.payload?.selectValue || null;
      state.adminAreaSettingsData = action.payload?.geoData || null;
    }
  }
});

export const {
  resetGlobalFilter,
  setSelectedAreaIds,
  setExplicitSelectedAreaIds,
  setSelectedAreaIdsTemp,
  setExplicitSelectedAreaIdsTemp,
  applyAdminTreeGlobalFilter,
  applyAdminTreeGlobalFilterTemp,
  setAdminAreaSettings,
  setAdminAreaSettingsData,
  setAdminAreaSettingsDataTemp,
  addLinkedDatasetId,
  removeLinkedDatasetId,
  setGlobalAdminArea
} = globalFilter.actions;

export const globalFilterReducer = globalFilter.reducer;
